import { useState } from "react";
import { useAlterarConta, useBancos, useConta } from "@/api/consultas";
import { recursoInexistente } from "@/api/problema";
import { useAvisos } from "@/componentes/Avisos";
import { Esqueleto } from "@/componentes/Esqueleto";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { useSessao } from "@/sessao/useSessao";
import { bancoDoCatalogo } from "./bancos";
import {
  camposDaConta,
  corpoDaEdicao,
  janelaAberta,
  regrasDoBanco,
  validarConta,
  valoresDe,
  type CampoDaConta,
  type ErrosDaConta,
  type ValoresDaConta,
} from "./conta";
import { FormularioDeConta } from "./FormularioDeConta";
import { errosDoFormulario, textoDaRecusa } from "./recusas";

/*
 * Edição de conta, aberta por `?abrir=editar-conta&conta={id}`, que é também o destino
 * da pendência `conta_incompleta`.
 *
 * A conta é lida de novo, e não herdada da linha: quem decide o que trava é a janela de
 * agora, e o modal pode vir de um link colado, sem lista nenhuma embaixo.
 *
 * Situação não mora aqui: é o interruptor da linha, que pede motivo e passa com a janela
 * fechada. Todo campo deste formulário é bancário, e por isso a janela fechada trava o
 * formulário inteiro: não há o que editar, e o modal fica de leitura, com um botão só.
 *
 * Um modal só, do carregando ao formulário: trocar de diálogo no meio faria o segundo
 * guardar como origem do foco um elemento do primeiro, e fechar deixaria o foco no
 * `body`. Por isso o estado é o que foi EDITADO, por cima da leitura.
 */
export function EditarConta({
  pessoaID,
  pessoaNome,
  contaID,
  aoFechar,
}: {
  pessoaID: string;
  pessoaNome: string;
  contaID: string;
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const leitura = useConta(cliente, pessoaID, contaID);
  const catalogo = useBancos(cliente, true);
  const alterar = useAlterarConta(cliente, pessoaID, contaID);

  const [editados, setEditados] = useState<Partial<ValoresDaConta>>({});
  const [erros, setErros] = useState<ErrosDaConta>({});
  const [erroGeral, setErroGeral] = useState<string | undefined>(undefined);

  const conta = leitura.data;
  const bancos = catalogo.data?.bancos ?? [];
  const regras = regrasDoBanco(bancoDoCatalogo(bancos, conta?.banco ?? ""));
  const aberta = conta !== undefined && janelaAberta(conta);

  const falhaDaLeitura = conta === undefined && leitura.error !== null;
  const carregando = (conta === undefined && !falhaDaLeitura) || catalogo.isPending;
  const somenteLeitura = !aberta && !carregando;

  function mudar(campo: CampoDaConta, valor: string) {
    setEditados((atuais) => ({ ...atuais, [campo]: valor }));
    setErros((atuais) => ({ ...atuais, [campo]: undefined }));
  }

  function enviar() {
    setErroGeral(undefined);
    if (conta === undefined || !aberta) return;

    const validacao = validarConta({ ...valoresDe(conta), ...editados }, regras);
    if (!validacao.ok) {
      setErros(validacao.erros);
      return;
    }

    // `PATCH` sem chave nenhuma é `400 invalid_body`: sem mudança, nada a pedir.
    const corpo = corpoDaEdicao(conta, validacao.conta);
    if (Object.keys(corpo).length === 0) {
      aoFechar();
      return;
    }

    setErros({});
    alterar.mutate(corpo, {
      onSuccess: () => {
        avisos.feito("Conta alterada");
        aoFechar();
      },
      onError: (falha) => {
        const recusa = errosDoFormulario(falha, camposDaConta(regras));
        setErros(recusa.porCampo);
        setErroGeral(recusa.geral);
      },
    });
  }

  return (
    <ModalDeCadastro
      aberto
      aoFechar={aoFechar}
      titulo={somenteLeitura ? "Dados da conta" : "Editar conta"}
      descricao={pessoaNome}
      aoEnviar={enviar}
      enviando={carregando || alterar.isPending}
      rotuloDeEnvio="Salvar"
      somenteLeitura={somenteLeitura}
      erro={
        falhaDaLeitura
          ? recursoInexistente(leitura.error)
            ? "Conta não encontrada."
            : textoDaRecusa(leitura.error)
          : erroGeral
      }
    >
      {carregando && <Esqueleto className="h-[280px]" />}

      {!carregando && conta !== undefined && (
        <FormularioDeConta
          valores={{ ...valoresDe(conta), ...editados }}
          erros={erros}
          aoMudar={mudar}
          bancos={bancos}
          carregandoBancos={false}
          falhaDosBancos={catalogo.error === null ? undefined : textoDaRecusa(catalogo.error)}
          bancoFixo
          travado={!aberta}
        />
      )}
    </ModalDeCadastro>
  );
}
